import PublicLayout from "@/components/PublicLayout";
import Modal from "@/components/Modal";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import {
  Plus,
  Trash2,
  ShieldCheck,
  AlertCircle,
  Feather,
  Church,
  Hourglass,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";

const EMOTIONS = ["Calm", "Anxious", "Tempted", "Angry", "Lonely", "Sad", "Tired", "Joyful"];

const FILTERS = [
  { id: "all", label: "All" },
  { id: "unconfessed", label: "Unconfessed" },
  { id: "confessed", label: "Confessed" },
];

function pad2(n) { return String(n).padStart(2, "0"); }

function nowLocalInputValue() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function isoToLocalInput(iso) {
  if (!iso) return nowLocalInputValue();
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return nowLocalInputValue();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function localInputToIso(local) {
  if (!local) return null;
  const d = new Date(local); // browser interprets datetime-local as local time
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

const buildEmptyForm = () => ({
  category: "Anger",
  severity: 3,
  trigger: "",
  emotional_state: "Calm",
  notes: "",
  resisted: false,
  recovery_actions: "",
  confessed: false,
  occurred_at_local: nowLocalInputValue(),
});

export default function SinTracker() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [counts, setCounts] = useState({ all: 0, confessed: 0, unconfessed: 0 });
  const [form, setForm] = useState(buildEmptyForm);

  const openCreate = (prefillCategory = null) => {
    setEditingId(null);
    setForm({
      ...buildEmptyForm(),
      category: prefillCategory || "Anger",
    });
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      category: item.category || "Anger",
      severity: item.severity || 3,
      trigger: item.trigger || "",
      emotional_state: item.emotional_state || "Calm",
      notes: item.notes || "",
      resisted: !!item.resisted,
      recovery_actions: item.recovery_actions || "",
      confessed: !!item.confessed,
      occurred_at_local: isoToLocalInput(item.occurred_at || item.created_at),
    });
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditingId(null);
    setForm(buildEmptyForm());
  };

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const [cats, list, allList] = await Promise.all([
        api.get("/bible/categories"),
        api.get(
          f === "all"
            ? "/sins"
            : f === "confessed"
            ? "/sins?confessed=true"
            : "/sins?confessed=false"
        ),
        f === "all" ? Promise.resolve({ data: { items: null } }) : api.get("/sins"),
      ]);
      setCategories(cats.data.categories);
      setItems(list.data.items);

      const allItems = f === "all" ? list.data.items : allList.data.items;
      const confessed = allItems.filter((s) => s.confessed).length;
      setCounts({
        all: allItems.length,
        confessed,
        unconfessed: allItems.length - confessed,
      });
    } catch (_e) {
      // ignore
    }
    setLoading(false);
  };

  useEffect(() => {
    load(filter);
  }, [filter]);

  // Open the modal with a prefilled category when navigated from the AI chat.
  useEffect(() => {
    const prefill = searchParams.get("prefill");
    if (prefill && categories.length > 0) {
      const safeCategory = categories.includes(prefill) ? prefill : "Other";
      openCreate(safeCategory);
      // remove the query param so it doesn't keep re-opening
      const next = new URLSearchParams(searchParams);
      next.delete("prefill");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, categories, setSearchParams]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      category: form.category,
      severity: form.severity,
      trigger: form.trigger,
      emotional_state: form.emotional_state,
      notes: form.notes,
      resisted: form.resisted,
      recovery_actions: form.recovery_actions,
      confessed: form.confessed,
      occurred_at: localInputToIso(form.occurred_at_local),
    };
    try {
      if (editingId) {
        await api.patch(`/sins/${editingId}`, payload);
        toast.success("Entry updated.");
      } else {
        await api.post("/sins", payload);
        toast.success(
          form.resisted
            ? "Praise God — that resistance was recorded."
            : "Logged. You are not alone."
        );
      }
      closeModal();
      load(filter);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/sins/${id}`);
      setItems(items.filter((x) => x.id !== id));
      toast.success("Entry removed.");
      load(filter);
    } catch (err) {
      toast.error("Could not remove entry.");
    }
  };

  const toggleConfession = async (id) => {
    try {
      const { data } = await api.patch(`/sins/${id}/confession`);
      toast.success(
        data.confessed
          ? "Marked as confessed. May the Lord grant you peace."
          : "Marked as unconfessed."
      );
      load(filter);
    } catch (err) {
      toast.error("Could not update confession status.");
    }
  };

  return (
    <PublicLayout>
      <section className="bg-brand-base">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-brand-quiet">Sin Tracker</p>
              <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-brand-ink tracking-tight">
                Be honest. Be free.
              </h1>
              <p className="mt-2 text-brand-quiet max-w-xl">
                Quietly name your struggles or the moments you stood firm. Mark each one as confessed
                when you have brought it before a priest.
              </p>
            </div>
            <button
              onClick={() => openCreate()}
              data-testid="sintracker-add-button"
              className="rounded-full bg-brand-sky text-white hover:bg-brand-skyDark transition-colors px-5 py-3 font-medium inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" strokeWidth={1.8} /> Record New Sin
            </button>
          </div>

          {/* Filter pills */}
          <div className="mt-7 flex items-center gap-2 flex-wrap" data-testid="sintracker-filters">
            {FILTERS.map((f) => {
              const isActive = filter === f.id;
              const count = counts[f.id] ?? 0;
              return (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  data-testid={`sintracker-filter-${f.id}`}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
                    isActive
                      ? "bg-brand-ink text-white border-brand-ink"
                      : "bg-white text-brand-quiet border-brand-line hover:bg-brand-mist"
                  }`}
                >
                  {f.id === "confessed" && <Church className="w-4 h-4" strokeWidth={1.6} />}
                  {f.id === "unconfessed" && <Hourglass className="w-4 h-4" strokeWidth={1.6} />}
                  {f.label}
                  <span
                    className={`text-[11px] rounded-full px-1.5 py-0.5 ${
                      isActive ? "bg-white/15 text-white" : "bg-brand-mist text-brand-quiet"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-5 rounded-2xl bg-white border border-brand-line">
            {loading ? (
              <div className="p-10 text-center text-brand-quiet">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-10 text-center" data-testid="sintracker-empty-state">
                <Feather className="w-8 h-8 text-brand-skyDark mx-auto mb-3" strokeWidth={1.4} />
                <p className="scripture-quote text-2xl text-brand-ink">
                  {filter === "confessed"
                    ? "“He is faithful and just to forgive us our sins.”"
                    : filter === "unconfessed"
                    ? "“Therefore confess your sins to one another, and pray for one another.”"
                    : "“Create in me a clean heart, O God.”"}
                </p>
                <p className="text-xs text-brand-muted mt-1">
                  {filter === "confessed"
                    ? "— 1 John 1:9"
                    : filter === "unconfessed"
                    ? "— James 5:16"
                    : "— Psalm 51:10"}
                </p>
                <p className="mt-4 text-sm text-brand-quiet">
                  {filter === "all"
                    ? "No entries yet. When you are ready, your private record begins here."
                    : filter === "confessed"
                    ? "No entries marked as confessed yet."
                    : "Nothing left unconfessed in your records."}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-brand-line">
                {items.map((it) => (
                  <SinRow
                    key={it.id}
                    item={it}
                    onDelete={() => remove(it.id)}
                    onEdit={() => openEdit(it)}
                    onToggleConfess={() => toggleConfession(it.id)}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <Modal open={open} onClose={closeModal} testid="sintracker-modal">
        <form
          onSubmit={submit}
          className="rounded-2xl bg-white border border-brand-line p-6 sm:p-8 space-y-5"
        >
            <div>
              <h2 className="font-serif text-3xl text-brand-ink">
                {editingId ? "Edit this entry" : "A moment to record"}
              </h2>
              <p className="text-sm text-brand-quiet mt-1">
                With grace, not shame. He already knows — this is for your healing.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <select
                  required
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  data-testid="sintracker-category-select"
                  className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-3 py-2.5 text-brand-ink"
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Severity (1–5)</Label>
                <input
                  type="range"
                  min={1}
                  max={5}
                  value={form.severity}
                  onChange={(e) => setForm({ ...form, severity: parseInt(e.target.value, 10) })}
                  data-testid="sintracker-severity-input"
                  className="w-full accent-brand-sky mt-3"
                />
                <p className="text-xs text-brand-quiet text-right">{form.severity}/5</p>
              </div>
            </div>

            <div>
              <Label>When did this happen?</Label>
              <input
                type="datetime-local"
                required
                value={form.occurred_at_local}
                max={nowLocalInputValue()}
                onChange={(e) => setForm({ ...form, occurred_at_local: e.target.value })}
                data-testid="sintracker-occurred-at-input"
                className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-3 py-2.5 text-brand-ink"
              />
              <p className="text-xs text-brand-muted mt-1">
                Defaults to right now. Change it if you’re recording something from earlier.
              </p>
            </div>

            <div>
              <Label>What triggered this?</Label>
              <input
                value={form.trigger}
                onChange={(e) => setForm({ ...form, trigger: e.target.value })}
                data-testid="sintracker-trigger-input"
                placeholder="e.g. argument, late night, phone scrolling"
                className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-3 py-2.5 text-brand-ink"
              />
            </div>

            <div>
              <Label>How were you feeling?</Label>
              <div className="flex flex-wrap gap-2">
                {EMOTIONS.map((em) => (
                  <button
                    key={em}
                    type="button"
                    data-testid={`emotion-${em.toLowerCase()}`}
                    onClick={() => setForm({ ...form, emotional_state: em })}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                      form.emotional_state === em
                        ? "bg-brand-sky text-white border-brand-sky"
                        : "bg-white text-brand-quiet border-brand-line hover:bg-brand-mist"
                    }`}
                  >
                    {em}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>How the sin happened?</Label>
              <textarea
                rows={4}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="sintracker-notes-input"
                placeholder="Describe what led to it, what you did, and how you felt afterward…"
                className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-3 py-2.5 text-brand-ink"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-brand-ink">
              <input
                type="checkbox"
                checked={form.resisted}
                onChange={(e) => setForm({ ...form, resisted: e.target.checked })}
                data-testid="sintracker-resisted-checkbox"
                className="accent-brand-sage"
              />
              I resisted the temptation this time.
            </label>

            {/* Confession switch */}
            <div className="rounded-xl border border-brand-line bg-brand-base p-3 flex items-center gap-3">
              <Church className="w-5 h-5 text-brand-skyDark shrink-0" strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-brand-ink">Confessed to a priest</p>
                <p className="text-xs text-brand-quiet">
                  Mark this once you have brought it to confession.
                </p>
              </div>
              <Switch
                checked={form.confessed}
                onChange={(v) => setForm({ ...form, confessed: v })}
                testid="sintracker-confessed-switch"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={closeModal}
                data-testid="sintracker-cancel-button"
                className="rounded-full px-5 py-2.5 text-brand-quiet hover:bg-brand-mist"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="sintracker-submit-button"
                className="rounded-full bg-brand-sky text-white hover:bg-brand-skyDark px-5 py-2.5 font-medium"
              >
                {editingId ? "Save changes" : "Save entry"}
              </button>
            </div>
          </form>
      </Modal>
    </PublicLayout>
  );
}

function SinRow({ item: it, onDelete, onEdit, onToggleConfess }) {
  return (
    <li
      data-testid={`sin-entry-${it.id}`}
      className="p-5 sm:p-6 flex flex-wrap gap-4 items-start"
    >
      <span
        className={`shrink-0 w-10 h-10 rounded-full grid place-items-center ${
          it.resisted
            ? "bg-[#e9f1ec] text-brand-sage"
            : "bg-[#fbeae6] text-brand-setback"
        }`}
      >
        {it.resisted ? (
          <ShieldCheck className="w-5 h-5" strokeWidth={1.6} />
        ) : (
          <AlertCircle className="w-5 h-5" strokeWidth={1.6} />
        )}
      </span>
      <div className="flex-1 min-w-[14rem]">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-brand-ink">{it.category}</span>
          <span className="text-xs text-brand-muted" data-testid={`sin-occurred-${it.id}`}>
            {new Date(it.occurred_at || it.created_at).toLocaleString()}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-brand-mist text-brand-quiet">
            {it.resisted ? "Resisted" : `Severity ${it.severity}/5`}
          </span>
          {it.emotional_state && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-brand-base border border-brand-line text-brand-quiet">
              {it.emotional_state}
            </span>
          )}
          {it.confessed ? (
            <span
              data-testid={`sin-confessed-badge-${it.id}`}
              className="text-xs px-2 py-0.5 rounded-full bg-[#e9f1ec] text-brand-sage inline-flex items-center gap-1"
            >
              <Church className="w-3 h-3" strokeWidth={1.7} /> Confessed
            </span>
          ) : (
            !it.resisted && (
              <span
                data-testid={`sin-unconfessed-badge-${it.id}`}
                className="text-xs px-2 py-0.5 rounded-full bg-[#fbf3e8] text-[#a07a3a] inline-flex items-center gap-1"
              >
                <Hourglass className="w-3 h-3" strokeWidth={1.7} /> Unconfessed
              </span>
            )
          )}
        </div>
        {it.trigger && (
          <p className="mt-2 text-sm text-brand-quiet">
            <span className="text-brand-ink/80 font-medium">Trigger:</span> {it.trigger}
          </p>
        )}
        {it.notes && <p className="mt-1 text-sm text-brand-quiet whitespace-pre-wrap">{it.notes}</p>}
        {it.confessed && it.confessed_at && (
          <p className="mt-1 text-xs text-brand-muted">
            Confessed on {new Date(it.confessed_at).toLocaleDateString()}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 self-start sm:self-center">
        <button
          onClick={onToggleConfess}
          data-testid={`sin-confess-toggle-${it.id}`}
          className={`inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border transition-colors ${
            it.confessed
              ? "bg-[#e9f1ec] text-brand-sage border-[#cbded2] hover:bg-[#dceae0]"
              : "bg-white text-brand-skyDark border-brand-line hover:bg-brand-mist"
          }`}
        >
          <Church className="w-3.5 h-3.5" strokeWidth={1.7} />
          {it.confessed ? "Confessed" : "Mark confessed"}
        </button>
        <button
          onClick={onEdit}
          data-testid={`sin-edit-${it.id}`}
          className="text-brand-quiet hover:text-brand-ink p-2 rounded-full hover:bg-brand-mist"
          aria-label="Edit entry"
        >
          <Pencil className="w-4 h-4" strokeWidth={1.6} />
        </button>
        <button
          onClick={onDelete}
          data-testid={`sin-delete-${it.id}`}
          className="text-brand-muted hover:text-brand-setback p-2 rounded-full hover:bg-brand-mist"
          aria-label="Delete entry"
        >
          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    </li>
  );
}

function Switch({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
        checked ? "bg-brand-sage" : "bg-brand-line"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function Label({ children }) {
  return (
    <label className="block text-xs uppercase tracking-wider text-brand-quiet mb-1.5">{children}</label>
  );
}
